/**
 * Tab Claustrophobia - Changes favicon to show increasing distress as more tabs are opened
 * 
 * This module uses the Page Visibility API and localStorage to detect and track
 * how many tabs of the site are currently open, then updates the favicon accordingly.
 */

// Define the different favicon states based on tab count
const FAVICON_STATES = [
  { count: 1, path: "/favicon/favicon.svg", description: "Normal favicon" },
  { count: 5, path: "/favicon/panic-level1.svg", description: "Slightly concerned favicon" },
  { count: 8, path: "/favicon/panic-level2.svg", description: "Worried favicon" },
  { count: 12, path: "/favicon/panic-level3.svg", description: "Panicked favicon" },
  { count: 15, path: "/favicon/panic-level4.svg", description: "Extremely distressed favicon" },
];

// Storage key for tab count
const TAB_COUNT_KEY = 'tog_open_tabs_count';

// Initialize the tab claustrophobia feature
export function initTabClaustrophobia() {
  // Register this tab when the page loads
  registerTab();
  
  // Update favicon based on current tab count
  updateFavicon();
  
  // Set up event listeners
  window.addEventListener('beforeunload', unregisterTab);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Check periodically for orphaned tabs (in case beforeunload didn't fire)
  setInterval(cleanupOrphanedTabs, 5000);
}

// Register this tab in localStorage
function registerTab() {
  try {
    // Generate a unique ID for this tab
    const tabId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    
    // Get existing tabs
    const openTabs = getOpenTabs();
    
    // Add this tab
    openTabs[tabId] = {
      timestamp: Date.now(),
      lastActive: Date.now()
    };
    
    // Save updated tabs
    localStorage.setItem(TAB_COUNT_KEY, JSON.stringify(openTabs));
    
    // Store the tab ID in memory
    window._tabClaustrophobiaId = tabId;
    
  } catch (error) {
    console.error('Error registering tab:', error);
  }
}

// Unregister this tab when it's closed
function unregisterTab() {
  try {
    if (!window._tabClaustrophobiaId) return;
    
    const openTabs = getOpenTabs();
    delete openTabs[window._tabClaustrophobiaId];
    
    localStorage.setItem(TAB_COUNT_KEY, JSON.stringify(openTabs));
  } catch (error) {
    console.error('Error unregistering tab:', error);
  }
}

// Handle visibility change (tab focus/blur)
function handleVisibilityChange() {
  try {
    if (!window._tabClaustrophobiaId) return;
    
    const openTabs = getOpenTabs();
    
    if (document.visibilityState === 'visible') {
      // Update last active timestamp when tab becomes visible
      if (openTabs[window._tabClaustrophobiaId]) {
        openTabs[window._tabClaustrophobiaId].lastActive = Date.now();
        localStorage.setItem(TAB_COUNT_KEY, JSON.stringify(openTabs));
      }
      
      // Update favicon when tab becomes visible
      updateFavicon();
    }
  } catch (error) {
    console.error('Error handling visibility change:', error);
  }
}

// Get all currently open tabs from localStorage
function getOpenTabs() {
  try {
    const tabsJson = localStorage.getItem(TAB_COUNT_KEY);
    return tabsJson ? JSON.parse(tabsJson) : {};
  } catch (error) {
    console.error('Error getting open tabs:', error);
    return {};
  }
}

// Clean up any orphaned tabs (tabs that haven't been active for a while)
function cleanupOrphanedTabs() {
  try {
    const openTabs = getOpenTabs();
    const now = Date.now();
    let changed = false;
    
    // Remove tabs that haven't been active for more than 5 minutes
    Object.keys(openTabs).forEach(tabId => {
      if (now - openTabs[tabId].lastActive > 5 * 60 * 1000) {
        delete openTabs[tabId];
        changed = true;
      }
    });
    
    if (changed) {
      localStorage.setItem(TAB_COUNT_KEY, JSON.stringify(openTabs));
      updateFavicon();
    }
    
    // If this is an active tab, update its timestamp
    if (document.visibilityState === 'visible' && window._tabClaustrophobiaId) {
      if (openTabs[window._tabClaustrophobiaId]) {
        openTabs[window._tabClaustrophobiaId].lastActive = now;
        localStorage.setItem(TAB_COUNT_KEY, JSON.stringify(openTabs));
      }
    }
  } catch (error) {
    console.error('Error cleaning up orphaned tabs:', error);
  }
}

// Update the favicon based on the number of open tabs
function updateFavicon() {
  try {
    const openTabs = getOpenTabs();
    const tabCount = Object.keys(openTabs).length;
    
    // Find the appropriate favicon state based on tab count
    let faviconState = FAVICON_STATES[0]; // Default to normal favicon
    
    for (let i = FAVICON_STATES.length - 1; i >= 0; i--) {
      if (tabCount >= FAVICON_STATES[i].count) {
        faviconState = FAVICON_STATES[i];
        break;
      }
    }
    
    // Update the favicon
    const faviconLinks = document.querySelectorAll('link[rel*="icon"]');
    
    // If no favicon links found, create one
    if (faviconLinks.length === 0) {
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconState.path;
      document.head.appendChild(link);
    } else {
      // Update existing favicon links
      faviconLinks.forEach(link => {
        if (link.getAttribute('data-original-href') === null) {
          // Store the original href the first time
          link.setAttribute('data-original-href', link.href);
        }
        
        // Only update SVG favicons to maintain compatibility
        if (link.href.endsWith('.svg') || link.getAttribute('data-original-href').endsWith('.svg')) {
          link.href = faviconState.path;
        }
      });
    }
    
    console.log(`Tab Claustrophobia: ${tabCount} tabs open, using ${faviconState.description}`);
  } catch (error) {
    console.error('Error updating favicon:', error);
  }
}